import React, { useState, useEffect, useRef, useCallback } from "react";
import { Navigate, useParams } from "react-router-dom";

import Grid from "@mui/material/Grid2";
import { Button } from "@mui/material";

import { 
  Campaign, 
  Character,
} from "@dndModels";
import API from "@dndUtilities/system_api";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";

import CenteredMenu from "@components/input/menus/CenteredMenu";
import LoadingBox from "@components/input/LoadingBox";

import ObjectDetails from "@dndComponents/ObjectDetails";
import CharacterOptionsInput from "@dndComponents/character/misc/CharacterOptionsInput";
import SendToCharacters from "@dndComponents/campaign/SendToCharacters";
import CampaignCharactersInput from "@dndComponents/campaign/CampaignCharactersInput";
import CampaignPlayersInput from "@dndComponents/campaign/CampaignPlayersInput";

const api = API.getInstance();
const char_util = DandDCharacterUtilities.getInstance();

export default function CampaignDetails() {
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [remove_characters_for_user_id, setRemoveCharactersForUserId] = useState("");
  const [fetched, setFetched] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [navigateTo, setNavigateTo] = useState<string | null>(null);
  const [obj, setObj] = useState(new Campaign());
  const [tab, setTab] = useState("Characters");
  const [open, setOpen] = useState(false);
  const [logging_in, setLoggingIn] = useState(false);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const check_login = useCallback( async () => {
    setTimeout(() => {
      if (api.user === null) {
        check_login();
      } else {
        setLoggingIn(false);
        load();
      }
    }, 1000);
  }, []);

  const loadObject = useCallback(async (campaign_id: string) => {
    // debugger;
    const campaign = await api.getCampaign({ campaign_id });
    if (campaign) {
      setObj(campaign);
    }
    setLoading(false);
  }, []);

  const load = useCallback(() => {
    console.log("load()");
    if (id && id !== "" && obj._id !== id) {
      loadObject(id);
    } else {
      setLoading(false);
    }
    setLoggingIn(false);
  }, [id, obj, loadObject]);
  
  useEffect(() => {
    if(!fetched) {
      setFetched(true);
      setLoading(true);
      if (api.user === null) {
        const userObjStr = localStorage.getItem("loginUser");
        if (!userObjStr) {
          setNavigateTo("/");
        } else {
          setLoggingIn(true);
          check_login();
        }
      } else {
        load();
      }
    }
  }, [fetched, load, check_login]);
    
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const updateCharacter = (obj: Character, onSuccess: Function) => {
    const user = api.user;
    if (user) {
      char_util.getCharacter({ id: obj._id }).then(res => {
        if (res) {
          res.old_campaign_id = obj.old_campaign_id;
          api.updateCharacter(res, ["main"]).then(res => {
            onSuccess();
          });
        }
      });
    }
  }

  const removeCharactersForUser = (user_id: string) => {
    const characters_for_user = obj.characters.filter(o => o.owner_id === user_id);
    if (characters_for_user.length > 0) {
      const char = characters_for_user[0];
      char.old_campaign_id = "";
      // char.active = true;
      updateCharacter(char, () => {
        const new_characters = obj.characters.filter(o => o.old_campaign_id === obj._id);
        setRemoveCharactersForUserId(user_id);
      });
    } else if (api.user && api.user._id === user_id) {
      setNavigateTo(`/campaign`);
    } 
  }

  useEffect(() => {
    if (remove_characters_for_user_id !== "") {
      const id = remove_characters_for_user_id;
      setRemoveCharactersForUserId("");
      removeCharactersForUser(id);
    }
  }, [remove_characters_for_user_id]);
  
  const renderExtras = () => {
    if (open) {
      return (
        <SendToCharacters
          campaign={obj}
          open
          onClose={() => { setOpen(false); }}
        />
      );
    }
    return null;
  }

  const renderTab = () => {
    if (tab === "Characters") {
      return (
        <Grid size={12}>
          <CampaignCharactersInput
            campaign={obj}
          />
        </Grid>
      );
    } else if (tab === "Players") {
      return (
        <Grid size={12}>
          <CampaignPlayersInput
            obj={obj}
          />
        </Grid>
      ); 
    } else if (tab === "Options") {
      return (
        <Grid size={12}>
          <CharacterOptionsInput 
            obj={obj}
            disabled
            onChange={() => { }}
          />
        </Grid>
      ); 
    }
    return (<Grid>Coming Soon</Grid>);
  }

  // console.log(api.user);
  if (navigateTo !== null) {
    return <Navigate to={navigateTo} />;
  } else if (logging_in) {
    return (
      <div className="centered">
        <LoadingBox message="Logging in" size={window.innerHeight * 0.5} height={window.innerHeight - 100} />
      </div>
    );
  } else if (api.user === null) {
    return (
      <div className="centered" style={{ height: "100%" }}>
        You must be logged in to view campaigns.
      </div>
    );
  } else if (!fetched || loading) {
    return (
      <div className="centered">
        <LoadingBox size={window.innerHeight * 0.5} height={window.innerHeight - 100} />
      </div>
    );
  } else if (api.user._id !== obj.owner_id && !obj.player_ids.includes(api.user._id)) {
    return (
      <div>
        You are not part of this campaign.
      </div>
    );
  } else { 
    const formHeight = window.innerHeight - (window.innerWidth > 600 ? 228 : 228);
    const tabs = ["Characters","Players","Options"];
    if (obj.c_options.use_recipes) {
      tabs.push("Recipes");
    }
    return (
      <Grid container spacing={0} direction="row">
        <Grid size={12}>
          <ObjectDetails 
            obj={obj}
            replace_edit={() => {
              if (api.user && api.user._id === obj.owner_id) {
                return (
                  <>
                    <Button variant="contained" color="primary" style={{marginLeft: "8px"}}
                      onClick={ () => {
                        setNavigateTo(`/campaign/gm_screen/${obj._id}`);
                      }}>
                      GM Screen
                    </Button>
                    {/* &nbsp;
                    <Button variant="contained" color="primary" style={{marginLeft: "8px"}}
                      onClick={ () => {
                        setNavigateTo(`/campaign/decks/${obj._id}`);
                      }}>
                      Decks for {obj.name}
                    </Button>
                    &nbsp;
                    <MailButton
                      onClick={() => {
                        setOpen(true);
                      }}
                    /> */}
                  </>
                );
              }
              return null;
            }}
          />
        </Grid>
        <Grid size={12}>
          <CenteredMenu
            options={tabs}
            selected={tab}
            onSelect={setTab}
          />
        </Grid>
        <Grid size={12} className="padded"
          style={{ 
            height: `${formHeight}px`, 
            overflowY: "scroll", 
            overflowX: "hidden" 
          }}>
          { renderTab() }
        </Grid>
        { renderExtras() }
      </Grid>
    );
  }
}
